/* ============================================================
   KAF Exchange — kaf-exchange.js · 2026-07-26 (manual flow)
   Eligibility → your details → the piece going back → the
   replacement → confirm. No order lookup: details are typed and
   matched by hand. The outgoing picker allows any variant, sold
   out included — the customer already owns it. The replacement
   picker is stock-guarded three ways: Liquid render, a live
   /products/{handle}.js check on select, and one more at send.
   Sending is a real Shopify contact-form POST (native, zero setup) so
   Shopify's spam challenge can render if it wants to; the WhatsApp
   handoff is stashed in sessionStorage and offered on the success
   screen. The Make webhook is a silent best-effort log.
   ============================================================ */
(function () {
  'use strict';

  var root = document.getElementById('kaf-ex');
  if (!root) return;

  /* ── Data ──────────────────────────────────────────────── */
  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  var PRODUCTS = readJSON('kaf-ex-products') || [];
  var CFG = readJSON('kaf-ex-config') || {};
  var FEE = parseFloat(CFG.deliveryFee);
  if (isNaN(FEE)) FEE = 2;
  var WA = CFG.whatsapp || '962782333434';
  var EMAIL = CFG.email || 'hello@wearekaf.com';

  var SWATCH = {
    black: { bg: '#202024', br: '#141417' },
    beige: { bg: '#E2DED4', br: '#C4BFB5' },
    white: { bg: '#F5F4EF', br: '#C0BDB8' },
    olive: { bg: '#6B7055', br: '#5A5E46' },
    sage:  { bg: '#9C9F8D', br: '#83866f' },
    stone: { bg: '#E2DED4', br: '#C4BFB5' },
    grey:  { bg: '#8E8E93', br: '#75757a' },
    gray:  { bg: '#8E8E93', br: '#75757a' },
    navy:  { bg: '#2A3244', br: '#1E2432' },
    brown: { bg: '#6B584A', br: '#54453a' }
  };

  /* ── Helpers ───────────────────────────────────────────── */
  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html !== undefined && html !== null) e.innerHTML = html;
    return e;
  }
  function esc(s) {
    return String(s === undefined || s === null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }

  /* money takes MAJOR units (27 → "27 JOD") */
  function money(v) {
    var n = Number(v);
    if (!isFinite(n)) n = 0;
    var abs = Math.abs(n);
    var s = abs % 1 === 0 ? String(abs) : abs.toFixed(2);
    return (n < 0 ? '-' : '') + s + ' JOD';
  }
  function swatchFor(name) {
    var key = String(name || '').trim().toLowerCase();
    return SWATCH[key] || { bg: '#909090', br: '#707070' };
  }
  function dotHTML(colour) {
    var c = swatchFor(colour);
    return '<span class="kaf-ex__dot" style="--sw-bg:' + c.bg + ';--sw-br:' + c.br + '" aria-hidden="true"></span>';
  }
  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function waLink(text) {
    return 'https://wa.me/' + WA + (text ? '?text=' + encodeURIComponent(text) : '');
  }

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* Jordanian mobile: 07XXXXXXXX, 7XXXXXXXX, +9627XXXXXXXX, 009627XXXXXXXX */
  function normalisePhone(raw) {
    var v = String(raw || '').replace(/[\s\-().]/g, '');
    if (/^00962/.test(v)) v = '+' + v.slice(2);
    if (/^962/.test(v)) v = '+' + v;
    var m = v.match(/^(?:\+962)?0?(7\d{8})$/);
    return m ? { ok: true, local: '0' + m[1], intl: '+962' + m[1] } : { ok: false };
  }

  /* ── Product option helpers ────────────────────────────── */
  function optIndex(product, name) {
    var opts = product.options || [];
    for (var i = 0; i < opts.length; i++) {
      if (String(opts[i]).toLowerCase() === name) return i;
    }
    return -1;
  }
  function optValues(product, name) {
    var idx = optIndex(product, name);
    if (idx < 0) return [];
    var seen = [];
    (product.variants || []).forEach(function (v) {
      var val = v.options[idx];
      if (val !== undefined && seen.indexOf(val) === -1) seen.push(val);
    });
    return seen;
  }
  function findVariant(product, colour, size) {
    var ci = optIndex(product, 'color');
    var si = optIndex(product, 'size');
    return (product.variants || []).filter(function (v) {
      return (ci < 0 || v.options[ci] === colour) && (si < 0 || v.options[si] === size);
    })[0] || null;
  }
  function sizeAvailable(product, colour, size) {
    var v = findVariant(product, colour, size);
    return !!(v && v.available);
  }
  /* alt convention: "… — Beige, front" */
  function imageFor(product, colour) {
    var imgs = product.images || [];
    var c = String(colour || '').toLowerCase();
    var i;
    if (c) {
      for (i = 0; i < imgs.length; i++) {
        var alt = String(imgs[i].alt || '').toLowerCase();
        if (alt.indexOf(c) !== -1 && alt.indexOf('front') !== -1) return imgs[i].src;
      }
      for (i = 0; i < imgs.length; i++) {
        if (String(imgs[i].alt || '').toLowerCase().indexOf(c) !== -1) return imgs[i].src;
      }
    }
    return product.image || (imgs[0] && imgs[0].src) || '';
  }
  function productByHandle(handle) {
    return PRODUCTS.filter(function (p) { return p.handle === handle; })[0] || null;
  }

  /* ── Live stock ────────────────────────────────────────── */
  var stockCache = {};
  var STOCK_TTL = 45000;

  function fetchLiveProduct(handle, fresh) {
    var cached = stockCache[handle];
    if (!fresh && cached && (Date.now() - cached.t) < STOCK_TTL) {
      return Promise.resolve(cached.p);
    }
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('stock lookup ' + res.status);
      return res.json();
    }).then(function (p) {
      stockCache[handle] = { t: Date.now(), p: p };
      return p;
    });
  }

  /* Fold live availability back into the render-time catalog. */
  function syncAvailability(handle, live) {
    var local = productByHandle(handle);
    if (!local || !live || !live.variants) return;
    live.variants.forEach(function (lv) {
      local.variants.forEach(function (v) {
        if (String(v.id) === String(lv.id)) {
          v.available = !!lv.available;
          if (typeof lv.inventory_quantity === 'number') v.qty = lv.inventory_quantity;
        }
      });
    });
  }

  /* Resolves { known, available }. A network failure is not proof of
     anything, so it resolves known:false and we trust the render-time
     value rather than blocking a real customer on a blip. */
  function liveAvailable(handle, variantId, fresh) {
    return fetchLiveProduct(handle, fresh).then(function (p) {
      syncAvailability(handle, p);
      var v = (p.variants || []).filter(function (x) { return String(x.id) === String(variantId); })[0];
      return { known: true, available: !!(v && v.available) };
    }).catch(function () {
      return { known: false, available: true };
    });
  }

  /* ── State ─────────────────────────────────────────────── */
  var S = {
    step: 0,
    orderNumber: '',
    orderName: '',
    name: '',
    email: '',
    phone: '',
    outItem: null,  /* {handle, title, color, size, price, image} */
    inItem: null,   /* {handle, title, color, size, price, variant_id, image} */
    sent: false,
    posting: false
  };

  /* ── Step machinery ────────────────────────────────────── */
  var steps = $$('[data-step]');
  var railItems = $$('[data-rail]');

  function stepEl(n) {
    return steps.filter(function (s) { return String(s.dataset.step) === String(n); })[0] || null;
  }
  function goTo(n, opts) {
    var target = stepEl(n);
    if (!target) return;
    steps.forEach(function (s) {
      var on = s === target;
      s.classList.toggle('is-active', on);
      s.hidden = !on;
    });
    S.step = n;
    var railIndex = Math.min(n, railItems.length - 1);
    railItems.forEach(function (r, i) {
      r.classList.toggle('is-current', i === railIndex && n < 5);
      r.classList.toggle('is-done', i < railIndex || n >= 5);
    });
    if (!opts || opts.scroll !== false) {
      var rail = $('.kaf-ex__rail');
      var offset = rail ? rail.getBoundingClientRect().height : 0;
      var hero = root.querySelector('.kaf-ex__hero');
      var top = window.pageYOffset + root.getBoundingClientRect().top + (hero ? hero.offsetHeight : 0) - offset;
      try {
        window.scrollTo({ top: Math.max(top, 0), behavior: reducedMotion() ? 'auto' : 'smooth' });
      } catch (e) {
        window.scrollTo(0, Math.max(top, 0));
      }
    }
    var heading = target.querySelector('.kaf-ex__step-title, .kaf-ex__done-title');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      try { heading.focus({ preventScroll: true }); } catch (e2) { /* noop */ }
    }
  }

  root.addEventListener('click', function (e) {
    var next = e.target.closest ? e.target.closest('[data-kaf-ex-next]') : null;
    if (next && !next.disabled) { goTo(parseInt(next.getAttribute('data-kaf-ex-next'), 10)); return; }
    var back = e.target.closest ? e.target.closest('[data-kaf-ex-back]') : null;
    if (back) { goTo(parseInt(back.getAttribute('data-kaf-ex-back'), 10)); }
  });

  /* ── STEP 0 · gate ─────────────────────────────────────── */
  var gate = $('[data-kaf-ex-gate]');
  var gateNext = $('[data-kaf-ex-next="1"]');
  if (gate && gateNext) {
    gate.addEventListener('change', function () { gateNext.disabled = !gate.checked; });
    gateNext.disabled = !gate.checked;
  }

  /* ── STEP 1 · details ──────────────────────────────────── */
  var detailsForm = $('[data-kaf-ex-details]');
  var orderInput = $('#kaf-ex-order');
  var nameInput = $('#kaf-ex-name');
  var emailInput = $('#kaf-ex-email');
  var phoneInput = $('#kaf-ex-phone');
  var notesInput = $('#kaf-ex-notes');

  function showErr(key, on) {
    var e = $('[data-kaf-ex-err="' + key + '"]');
    if (e) e.hidden = !on;
  }
  function markField(input, bad) {
    if (input) input.classList.toggle('has-error', !!bad);
  }

  if (detailsForm) {
    detailsForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var order = (orderInput.value || '').trim().replace(/^#/, '');
      var name = (nameInput.value || '').trim();
      var email = (emailInput.value || '').trim();
      var phoneRes = normalisePhone(phoneInput.value);
      var bad = false;
      if (!order) { showErr('order', true); markField(orderInput, true); bad = true; }
      else { showErr('order', false); markField(orderInput, false); }
      if (!name) { showErr('name', true); markField(nameInput, true); bad = true; }
      else { showErr('name', false); markField(nameInput, false); }
      if (!EMAIL_RE.test(email)) { showErr('email', true); markField(emailInput, true); bad = true; }
      else { showErr('email', false); markField(emailInput, false); }
      if (!phoneRes.ok) { showErr('phone', true); markField(phoneInput, true); bad = true; }
      else { showErr('phone', false); markField(phoneInput, false); }
      if (bad) return;

      S.orderNumber = order;
      S.orderName = '#' + order;
      S.name = name;
      S.email = email;
      S.phone = phoneRes.local;
      goTo(2);
    });
  }

  /* ── Catalog picker ────────────────────────────────────── */
  /* mode 'out' — the piece going back. Every variant selectable:
     current stock is irrelevant, the customer already owns it.
     mode 'in'  — the replacement. Sold out disabled, live-checked. */
  function buildPicker(mount, mode) {
    mount.innerHTML = '';
    if (!PRODUCTS.length) {
      mount.appendChild(el('p', 'kaf-ex__note',
        'The collection is not loading right now. Message us on WhatsApp and we will handle your exchange there.'));
      return;
    }
    var cards = [];

    PRODUCTS.forEach(function (product, pi) {
      var colours = optValues(product, 'color');
      var sizes = optValues(product, 'size');
      var st = { colour: colours[0] || null, size: null, open: false };

      var card = el('article', 'kaf-ex__prod');
      var top = el('button', 'kaf-ex__prod-top');
      top.type = 'button';
      top.setAttribute('aria-expanded', 'false');

      var img = el('img', 'kaf-ex__prod-img');
      img.src = imageFor(product, st.colour);
      img.alt = '';
      img.loading = pi < 3 ? 'eager' : 'lazy';
      img.decoding = 'async';
      img.width = 54; img.height = 68;
      top.appendChild(img);

      var meta = el('div', 'kaf-ex__prod-meta');
      meta.appendChild(el('p', 'kaf-ex__prod-title', esc(product.title)));
      meta.appendChild(el('div', 'kaf-ex__prod-price', money((Number(product.price) || 0) / 100)));
      top.appendChild(meta);
      top.appendChild(el('span', 'kaf-ex__prod-chev',
        '<svg width="11" height="11" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'));
      card.appendChild(top);

      var body = el('div', 'kaf-ex__prod-body');
      body.hidden = true;
      card.appendChild(body);

      var swWrap = null;
      if (colours.length) {
        var swBlock = el('div', '');
        swBlock.appendChild(el('p', 'kaf-ex__row-label', 'Colour'));
        swWrap = el('div', 'kaf-ex__swatches');
        swBlock.appendChild(swWrap);
        body.appendChild(swBlock);
      }

      var szWrap = null;
      if (sizes.length) {
        var szBlock = el('div', '');
        szBlock.appendChild(el('p', 'kaf-ex__row-label', 'Size'));
        szWrap = el('div', 'kaf-ex__sizes');
        szBlock.appendChild(szWrap);
        body.appendChild(szBlock);
      }

      var pickBtn = el('button', 'kaf-ex__pick');
      pickBtn.type = 'button';
      pickBtn.disabled = true;
      body.appendChild(pickBtn);

      var note = el('p', 'kaf-ex__prod-note');
      note.hidden = true;
      body.appendChild(note);

      function pickLabel() { return mode === 'out' ? 'THIS IS MY PIECE' : 'SWAP INTO THIS'; }

      function paintSwatches() {
        if (!swWrap) return;
        swWrap.innerHTML = '';
        colours.forEach(function (c) {
          var conf = swatchFor(c);
          var b = el('button', 'kaf-ex__swatch' + (c === st.colour ? ' is-active' : ''));
          b.type = 'button';
          b.style.setProperty('--sw-bg', conf.bg);
          b.style.setProperty('--sw-br', conf.br);
          b.setAttribute('aria-label', product.title + ' colour: ' + c);
          b.setAttribute('aria-pressed', c === st.colour ? 'true' : 'false');
          b.addEventListener('click', function () {
            st.colour = c;
            if (mode === 'in' && st.size && !sizeAvailable(product, st.colour, st.size)) st.size = null;
            img.src = imageFor(product, st.colour);
            note.hidden = true;
            paintSwatches();
            paintSizes();
            paintPick();
          });
          swWrap.appendChild(b);
        });
      }

      function paintSizes() {
        if (!szWrap) return;
        szWrap.innerHTML = '';
        sizes.forEach(function (s) {
          /* 'out': always selectable — the piece is already theirs */
          var avail = mode === 'out' ? true : sizeAvailable(product, st.colour, s);
          var b = el('button', 'kaf-ex__size' + (s === st.size ? ' is-active' : '') + (avail ? '' : ' is-sold-out'), esc(s));
          b.type = 'button';
          b.disabled = !avail;
          b.setAttribute('aria-label', avail ? s : s + ' — sold out');
          b.setAttribute('aria-pressed', s === st.size ? 'true' : 'false');
          b.addEventListener('click', function () {
            st.size = s;
            note.hidden = true;
            paintSizes();
            paintPick();
          });
          szWrap.appendChild(b);
        });
      }

      function currentVariant() { return findVariant(product, st.colour, st.size); }

      function isPicked() {
        var chosen = mode === 'out' ? S.outItem : S.inItem;
        if (!chosen) return false;
        return chosen.handle === product.handle && chosen.color === st.colour && chosen.size === st.size;
      }

      function paintPick() {
        var ready = (!colours.length || !!st.colour) && (!sizes.length || !!st.size);
        if (mode === 'in') ready = ready && !!currentVariant();
        pickBtn.disabled = !ready;
        var picked = isPicked();
        pickBtn.classList.toggle('is-picked', picked);
        card.classList.toggle('is-picked', picked);
        pickBtn.textContent = picked ? 'SELECTED' : pickLabel();
      }

      function commit() {
        var v = currentVariant();
        var priceMajor = v ? (Number(v.price) || 0) / 100 : (Number(product.price) || 0) / 100;
        var payload = {
          handle: product.handle,
          title: product.title,
          color: st.colour || '',
          size: st.size || '',
          price: priceMajor,
          image: imageFor(product, st.colour)
        };
        if (mode === 'in') {
          payload.variant_id = v ? v.id : null;
          S.inItem = payload;
          renderMath();
          var nextBtn = $('[data-kaf-ex-next="4"]');
          if (nextBtn) nextBtn.disabled = false;
          setStockNote('');
        } else {
          S.outItem = payload;
          var outNext = $('[data-kaf-ex-next="3"]');
          if (outNext) outNext.disabled = false;
          renderMath();
        }
        cards.forEach(function (c) { c.repaint(); });
      }

      pickBtn.addEventListener('click', function () {
        if (mode === 'out') { commit(); return; }
        var v = currentVariant();
        if (!v) return;
        pickBtn.classList.add('is-checking');
        pickBtn.textContent = 'CHECKING STOCK';
        liveAvailable(product.handle, v.id, true).then(function (res) {
          pickBtn.classList.remove('is-checking');
          if (res.available) {
            commit();
            return;
          }
          /* Just sold out — fold it in, clear the choice, keep every door open. */
          var goneSize = st.size;
          st.size = null;
          if (S.inItem && S.inItem.handle === product.handle) {
            S.inItem = null;
            var nb = $('[data-kaf-ex-next="4"]');
            if (nb) nb.disabled = true;
            renderMath();
          }
          paintSizes();
          paintPick();
          note.innerHTML = '<strong>' + esc(goneSize) + '</strong> just sold out. Pick another size or colour.';
          note.hidden = false;
          setStockNote('<strong>' + esc(product.title) + ' — ' + esc(st.colour || '') + ', ' + esc(goneSize) +
            '</strong> went out of stock while you were looking. Everything else below is live.');
        });
      });

      top.addEventListener('click', function () {
        st.open = !st.open;
        body.hidden = !st.open;
        card.classList.toggle('is-open', st.open);
        top.setAttribute('aria-expanded', st.open ? 'true' : 'false');
        /* Warm the live stock cache the moment a card is opened. */
        if (st.open && mode === 'in') {
          fetchLiveProduct(product.handle, false).then(function (live) {
            syncAvailability(product.handle, live);
            paintSizes();
            paintPick();
          }).catch(function () { /* render-time data stands */ });
        }
      });

      paintSwatches();
      paintSizes();
      paintPick();

      cards.push({ repaint: function () { paintPick(); }, refreshSizes: function () { paintSizes(); paintPick(); } });
      mount.appendChild(card);
    });

    return cards;
  }

  var outPicker = $('[data-kaf-ex-out-picker]');
  var inPicker = $('[data-kaf-ex-in-picker]');
  var inCards = null;
  if (outPicker) buildPicker(outPicker, 'out');
  if (inPicker) inCards = buildPicker(inPicker, 'in');

  /* ── Stock notice ──────────────────────────────────────── */
  var stockNote = $('[data-kaf-ex-stock-note]');
  function setStockNote(html) {
    if (!stockNote) return;
    if (!html) { stockNote.hidden = true; stockNote.innerHTML = ''; return; }
    stockNote.innerHTML = html;
    stockNote.hidden = false;
  }

  /* ── The math ──────────────────────────────────────────── */
  var mathBox = $('[data-kaf-ex-math]');

  function priceDiff() {
    if (!S.outItem || !S.inItem) return 0;
    return Math.round(((Number(S.inItem.price) || 0) - (Number(S.outItem.price) || 0)) * 1000) / 1000;
  }

  /* Single source of truth for the on-page summary and the WhatsApp message. */
  function settlement() {
    var diff = priceDiff();
    var net = Math.round((FEE + diff) * 1000) / 1000;
    if (diff > 0) {
      return {
        diff: diff, diffLabel: '+' + money(diff),
        totalLabel: 'To pay', totalAmount: net,
        note: 'We confirm on WhatsApp and send a payment link, a CliQ number, or take it in cash when the courier hands over your new piece.'
      };
    }
    if (diff < 0 && net > 0) {
      return {
        diff: diff, diffLabel: money(diff),
        totalLabel: 'To pay', totalAmount: net,
        note: 'Your replacement costs less, so we settle the ' + money(Math.abs(diff)) + ' difference against the ' + money(FEE) + ' delivery fee. Anything owed either way is confirmed on WhatsApp — refunds go back via CliQ.'
      };
    }
    if (diff < 0 && net === 0) {
      return {
        diff: diff, diffLabel: money(diff),
        totalLabel: 'To pay', totalAmount: 0,
        note: 'Your replacement costs ' + money(Math.abs(diff)) + ' less, which covers the ' + money(FEE) + ' delivery fee exactly. Nothing to pay either way.'
      };
    }
    if (diff < 0) {
      return {
        diff: diff, diffLabel: money(diff),
        totalLabel: 'Back to you', totalAmount: Math.abs(net),
        note: 'Your replacement costs less. We refund the ' + money(Math.abs(diff)) + ' difference via CliQ after the ' + money(FEE) + ' delivery fee, and confirm the details with you on WhatsApp first.'
      };
    }
    return {
      diff: 0, diffLabel: 'None',
      totalLabel: 'To pay', totalAmount: FEE,
      note: 'No price difference — only the ' + money(FEE) + ' delivery fee applies.'
    };
  }

  function renderMath() {
    if (!mathBox) return;
    if (!S.outItem || !S.inItem) { mathBox.hidden = true; mathBox.innerHTML = ''; return; }
    var s = settlement();
    var rows = '';
    rows += '<div class="kaf-ex__sum-row"><span>' + esc(S.outItem.title) + ' — going back</span><span>' + money(S.outItem.price) + '</span></div>';
    rows += '<div class="kaf-ex__sum-row"><span>' + esc(S.inItem.title) + ' — replacement</span><span>' + money(S.inItem.price) + '</span></div>';
    rows += '<div class="kaf-ex__sum-row"><span>Price difference</span><span>' + s.diffLabel + '</span></div>';
    rows += '<div class="kaf-ex__sum-row"><span>Delivery fee</span><span>' + money(FEE) + '</span></div>';
    rows += '<div class="kaf-ex__sum-row kaf-ex__sum-row--total"><span>' + s.totalLabel + '</span><span>' + money(s.totalAmount) + '</span></div>';

    mathBox.innerHTML =
      '<p class="kaf-ex__sum-title">The maths</p>' + rows +
      '<p class="kaf-ex__sum-note">' + s.note + '</p>' +
      '<p class="kaf-ex__sum-fine">Final amount confirmed on WhatsApp against your original order.</p>';
    mathBox.hidden = false;
  }

  /* ── STEP 4 · recap ────────────────────────────────────── */
  var recap = $('[data-kaf-ex-recap]');
  var submitForm = $('form#kaf-ex-contact') || $('form[action*="/contact"]');
  var sendBtn = $('[data-kaf-ex-send-btn]');
  var submitNote = $('[data-kaf-ex-submit-note]');
  var doneBox = $('[data-kaf-ex-done]');

  function sideHTML(tag, item) {
    return '' +
      '<div class="kaf-ex__recap-side">' +
        '<img class="kaf-ex__recap-img" src="' + esc(item.image || '') + '" alt="" width="48" height="60" loading="lazy" decoding="async">' +
        '<div>' +
          '<span class="kaf-ex__recap-tag">' + esc(tag) + '</span>' +
          '<div class="kaf-ex__recap-name">' + esc(item.title) + '</div>' +
          '<div class="kaf-ex__recap-sub">' + (item.color ? dotHTML(item.color) + '<span>' + esc(item.color) + '</span>' : '') +
            (item.size ? '<span>· ' + esc(item.size) + '</span>' : '') +
            '<span>· ' + money(item.price) + '</span>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function renderRecap() {
    if (!recap || !S.outItem || !S.inItem) return;
    recap.innerHTML =
      sideHTML('Going back', S.outItem) +
      '<span class="kaf-ex__recap-arrow" aria-hidden="true">&rarr;</span>' +
      sideHTML('Replacing with', S.inItem) +
      '<div class="kaf-ex__recap-who">Order <strong>' + esc(S.orderName) + '</strong> · ' +
        esc(S.name) + ' · ' + esc(S.phone) + ' · ' + esc(S.email) + '</div>';
  }

  var step3Next = $('[data-kaf-ex-next="4"]');
  if (step3Next) {
    step3Next.addEventListener('click', function () {
      renderRecap();
      if (mathBox && !mathBox.hidden) {
        var clone = mathBox.cloneNode(true);
        clone.removeAttribute('data-kaf-ex-math');
        clone.hidden = false;
        var host = $('[data-step="4"] .kaf-ex__recap');
        var existing = $('[data-step="4"] .kaf-ex__summary');
        if (existing && existing.parentNode) { existing.parentNode.replaceChild(clone, existing); }
        else if (host && host.parentNode) { host.parentNode.insertBefore(clone, host.nextSibling); }
      }
    });
  }

  /* ── The request, written once ─────────────────────────── */
  /* kind 'email' adds the variant id and the source marker; the
     WhatsApp copy stays human. Both read identically otherwise. */
  function requestText(kind) {
    var s = settlement();
    var notes = notesInput && notesInput.value ? notesInput.value.trim() : '';
    var lines = [];
    lines.push('KAF EXCHANGE REQUEST — ' + S.orderName + ' — ' + S.name);
    lines.push('');
    lines.push('Order: ' + S.orderName);
    lines.push('Name: ' + S.name);
    lines.push('Phone: ' + S.phone);
    lines.push('Email: ' + S.email);
    lines.push('');
    lines.push('Going back: ' + S.outItem.title + ' — ' + S.outItem.color + ', ' + S.outItem.size + ' (' + money(S.outItem.price) + ')');
    lines.push('Replacement: ' + S.inItem.title + ' — ' + S.inItem.color + ', ' + S.inItem.size + ' (' + money(S.inItem.price) + ')');
    if (kind === 'email' && S.inItem.variant_id) {
      lines.push('Replacement variant id: ' + S.inItem.variant_id);
    }
    lines.push('');
    lines.push('Price difference: ' + (s.diff === 0 ? 'none' : s.diffLabel));
    lines.push('Delivery: ' + money(FEE));
    lines.push(s.totalLabel + ': ' + money(s.totalAmount));
    if (notes) {
      lines.push('');
      lines.push('Notes: ' + notes);
    }
    if (kind === 'email') {
      lines.push('');
      lines.push('Source: exchange page');
    }
    return lines.join('\n');
  }

  /* ── Success stash: survives the contact-form round trip ── */
  var STASH_KEY = 'kaf-ex-request';
  var STASH_TTL = 30 * 60 * 1000;

  function stashRequest(waUrl) {
    try {
      sessionStorage.setItem(STASH_KEY, JSON.stringify({
        v: 1,
        t: Date.now(),
        wa: waUrl,
        orderName: S.orderName,
        name: S.name
      }));
    } catch (e) { /* private mode — the generic success state covers it */ }
  }
  function readStash() {
    try {
      var raw = sessionStorage.getItem(STASH_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      if (!d || d.v !== 1 || !d.wa) return null;
      if (Date.now() - (d.t || 0) > STASH_TTL) return null;
      return d;
    } catch (e) { return null; }
  }

  /* ── Best-effort log. Never blocks, never surfaces. ────── */
  function logRequest() {
    var url = (CFG.submitUrl || '').trim();
    if (!url) return;
    var s = settlement();
    var payload = {
      order_number: S.orderNumber,
      order_name: S.orderName,
      phone: S.phone,
      name: S.name,
      swap_out: {
        title: S.outItem.title,
        color: S.outItem.color,
        size: S.outItem.size,
        price: S.outItem.price
      },
      swap_in: {
        title: S.inItem.title,
        color: S.inItem.color,
        size: S.inItem.size,
        price: S.inItem.price,
        variant_id: S.inItem.variant_id || null
      },
      price_difference: s.diff,
      delivery_fee: FEE,
      notes: (notesInput && notesInput.value ? notesInput.value.trim() : ''),
      email: S.email,
      source: 'storefront_manual'
    };
    try {
      var p = fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(payload),
        keepalive: true
      });
      if (p && typeof p.catch === 'function') p.catch(function () { /* silent by design */ });
    } catch (e) { /* silent by design */ }
  }

  /* ── Send ──────────────────────────────────────────────── */
  function bounceToStock(item) {
    /* The replacement went while they were deciding. Calm, no dead end. */
    var product = productByHandle(item.handle);
    if (product) {
      var v = findVariant(product, item.color, item.size);
      if (v) v.available = false;
    }
    S.inItem = null;
    if (step3Next) step3Next.disabled = true;
    renderMath();
    if (inCards) inCards.forEach(function (c) { c.refreshSizes(); });
    setStockNote('<strong>' + esc(item.title) + ' — ' + esc(item.color) + ', ' + esc(item.size) +
      '</strong> sold out in the last minute. Pick another size or colour and everything else you entered is still here.');
    goTo(3);
  }

  if (submitForm) {
    submitForm.addEventListener('submit', function (e) {
      /* Second pass: everything is filled and checked, so let the event run.
         Shopify's storefront-forms captcha script also listens for submit on
         any form[action*='/contact'] — it needs a real event to bind its
         token and, if it wants to, hand off to the /challenge page. Calling
         form.submit() directly would skip it entirely. */
      if (S.posting) return;

      e.preventDefault();
      if (S.sent) return;
      if (!S.outItem || !S.inItem) {
        if (submitNote) submitNote.textContent = 'Go back and pick both pieces first.';
        return;
      }
      var item = S.inItem;
      sendBtn.classList.add('is-loading');
      sendBtn.disabled = true;
      if (submitNote) submitNote.textContent = 'Checking stock one last time…';

      liveAvailable(item.handle, item.variant_id, true).then(function (res) {
        if (!res.available) {
          sendBtn.classList.remove('is-loading');
          sendBtn.disabled = false;
          if (submitNote) submitNote.textContent = '';
          bounceToStock(item);
          return;
        }

        if (submitNote) submitNote.textContent = 'Sending your request…';

        /* Fill the native contact fields Shopify will email us. */
        setVal('[data-kaf-ex-c-name]', S.name);
        setVal('[data-kaf-ex-c-email]', S.email);
        setVal('[data-kaf-ex-c-phone]', S.phone);
        setVal('[data-kaf-ex-c-body]', requestText('email'));

        /* Best-effort log first — keepalive survives the navigation. */
        logRequest();

        /* Stash the WhatsApp handoff so it survives the round trip
           (and any /challenge captcha Shopify interposes). */
        stashRequest(waLink(requestText('whatsapp')));

        S.sent = true;
        S.posting = true;
        /* Re-dispatch a REAL submit event so Shopify's captcha binder runs;
           requestSubmit fires it, form.submit() would not. */
        if (typeof submitForm.requestSubmit === 'function') {
          submitForm.requestSubmit();
        } else {
          /* Older Safari: a synthetic submit button click fires the event too. */
          var ghost = document.createElement('button');
          ghost.type = 'submit';
          ghost.style.display = 'none';
          submitForm.appendChild(ghost);
          ghost.click();
        }
      });
    });
  }

  function setVal(sel, value) {
    var f = $(sel, submitForm) || $(sel);
    if (f) f.value = value;
  }

  function renderDone(waUrl, opts) {
    if (!doneBox) return;
    opts = opts || {};
    doneBox.innerHTML = '';
    doneBox.appendChild(el('span', 'kaf-ex__done-mark',
      '<svg width="20" height="20" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2 7L5.5 10.5L12 3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>'));
    doneBox.appendChild(el('h2', 'kaf-ex__done-title', 'Request sent.'));
    doneBox.appendChild(el('p', 'kaf-ex__done-body',
      (opts.orderName ? 'Order <strong>' + esc(opts.orderName) + '</strong> — it is in our inbox. ' : 'It is in our inbox. ') +
      'We reply <strong>within 24 hours</strong> with the pickup time, the ' + money(FEE) +
      ' delivery fee, and anything owed either way. Keep the piece unworn with its tags and sleeve until the courier arrives.'));

    var actions = el('div', 'kaf-ex__actions');
    var wa = el('a', 'kaf-ex__btn kaf-ex__btn--primary', 'CONFIRM ON WHATSAPP');
    wa.href = waUrl;
    wa.target = '_blank';
    wa.rel = 'noopener';
    actions.appendChild(wa);
    var shop = el('a', 'kaf-ex__btn', 'BACK TO THE COLLECTION');
    shop.href = '/collections/all';
    actions.appendChild(shop);
    doneBox.appendChild(actions);

    doneBox.appendChild(el('p', 'kaf-ex__done-aside',
      'Your request is already with us by email — WhatsApp is optional. One tap sends us the same details there and usually gets a faster reply.'));
    goTo(5);
  }

  /* ── Boot ──────────────────────────────────────────────── */
  function contactPosted() {
    return /(^|[?&])contact_posted=true(&|$)/.test(window.location.search);
  }

  if (contactPosted()) {
    var stash = readStash();
    S.sent = true;
    if (stash) {
      renderDone(stash.wa, { orderName: stash.orderName });
    } else {
      /* Landed back with an empty stash — different tab, private mode, or
         an expired session. Still a success, just without the specifics. */
      renderDone(waLink('Hi KAF — I just sent an exchange request through the site.'), {});
    }
  } else {
    goTo(0, { scroll: false });
  }
})();
